import React from 'react';
import { View, Text, Platform } from 'react-native';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import AmountInput from '@/components/Giving/AmountInput';

/**
 * Branded hero block for the giving flow: org identity + large tappable amount
 * with preset chips. Rounded card on all platforms; web centering/max-width is
 * handled by the parent screen's content column.
 */
export default function GivingHero({
    orgName,
    logoUrl = null,
    showIdentity = true,
    baseColor = null,
    gradientColors = null,
    heroTextColor = null,
    surfaceColor = null,
    amountCents = 0,
    onAmountChange,
    testID = 'giving-hero',
    amountTestID = 'amount-input',
}) {
    const { theme: { colors } } = useUnistyles();
    const initial = (orgName || '?').trim().charAt(0).toUpperCase();
    const resolvedGradient = gradientColors || [colors.givingHeroStart, colors.givingHeroEnd];

    return (
        <View
            style={[
                styles.hero,
                Platform.OS === 'web' && gradientColors && { backgroundImage: `linear-gradient(135deg, ${resolvedGradient[0]}, ${resolvedGradient[1]})` },
            ]}
            testID={testID}
        >
            {Platform.OS !== 'web' && (
                <LinearGradient
                    colors={resolvedGradient}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={styles.heroGradient}
                />
            )}
            <View style={styles.inner}>
                {showIdentity && <View style={styles.identityRow}>
                    {logoUrl ? (
                        <View style={[styles.logoChip, surfaceColor && { backgroundColor: surfaceColor }]} testID={`${testID}-logo`}>
                            <Image
                                source={{ uri: logoUrl }}
                                style={{ width: 40, height: 40 }}
                                contentFit="contain"
                                transition={250}
                            />
                        </View>
                    ) : (
                        <View style={[styles.logoChip, surfaceColor && { backgroundColor: surfaceColor }]} testID={`${testID}-monogram`}>
                            <Text style={[styles.monogram, baseColor && { color: baseColor }]}>{initial}</Text>
                        </View>
                    )}
                    <Text style={[styles.orgName, heroTextColor && { color: heroTextColor }]} numberOfLines={2} testID={`${testID}-org-name`}>
                        {orgName}
                    </Text>
                </View>}
                <AmountInput
                    variant="hero"
                    heroTextColor={heroTextColor}
                    testID={amountTestID}
                    amountCents={amountCents}
                    onChange={onAmountChange}
                />
            </View>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, typography }) => ({
    hero: {
        borderRadius: roundness.lg,
        overflow: 'hidden',
        _web: {
            backgroundImage: `linear-gradient(135deg, ${colors.givingHeroStart}, ${colors.givingHeroEnd})`,
        },
    },
    heroGradient: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    inner: {
        paddingVertical: spacing.lg,
        paddingHorizontal: spacing.md,
        gap: spacing.md,
    },
    identityRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },
    logoChip: {
        width: 48,
        height: 48,
        borderRadius: roundness.md,
        backgroundColor: colors.surface,
        alignItems: 'center',
        justifyContent: 'center',
    },
    monogram: {
        fontSize: typography.h1,
        fontFamily: fonts.heading,
        color: colors.giving,
    },
    orgName: {
        flex: 1,
        fontSize: typography.h2,
        fontFamily: fonts.heading,
        color: colors.onGivingHero,
    },
}));
